'use client';

import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, useDeferredValue, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { AuthQueryOptions } from '@/api';

import { usePartnersMember } from '../../_hooks/usePartnersMember';
import { formatWon } from '../../_utils/labels';
import { contextOf } from '../../_utils/queries';
import type { ReportFilter } from '../../_utils/report/report';
import {
  filterOfSearchParams,
  filterToLinkQuery,
  firstDateOf,
  rangeLabelOf,
  replaceFilterUrl,
  resolvedFilterOf,
  todayOf,
} from '../../_utils/report/report';
import type { PartnersLinkList } from '../../_utils/types';
import { LinkCard } from '../link-card/LinkCard';
import { PartnersDialogs } from '../PartnersDialogs';
import { PartnersGate } from '../PartnersGate';
import { PartnersShell } from '../PartnersShell';
import { ReportFilterBar } from '../report/ReportFilterBar';
import { SectionLoadError } from '../SectionLoadError';

interface ContentsBodyProps {
  list: PartnersLinkList | null;
  listFilter: ReportFilter;
  filterBar: React.ReactNode;
}

const ContentsBody = ({ list, listFilter, filterBar }: ContentsBodyProps) => (
  <div className="flex flex-col gap-20 px-20 pb-24 xl:px-0">
    <p className="flex items-center justify-between gap-8 rounded-small bg-Gray-25 px-16 py-12 typo-body4">
      <span className="text-Gray-900">
        링크 {list ? list.contents.length : '-'}개
      </span>
      <span className="text-B-550">
        총 {list ? list.donorCount.toLocaleString('ko-KR') : '-'}명 ·{' '}
        {list ? formatWon(list.rewardAmount) : '-'}
      </span>
    </p>
    {filterBar}
    {list?.contents.length === 0 ? (
      <p className="flex min-h-120 flex-col items-center justify-center gap-4 text-center">
        <span className="font-700 text-Gray-800 typo-body3">
          콘텐츠별 실적이 아직 없어요
        </span>
        <span className="text-Gray-600 typo-caption1">
          링크를 콘텐츠에 넣으면 콘텐츠마다 실적을 볼 수 있어요.
        </span>
      </p>
    ) : null}
    {list && list.contents.length > 0 ? (
      <div className="flex flex-col gap-8">
        {list.contents.map((link) => (
          <LinkCard
            key={link.partnersLinkId}
            link={link}
            rangeLabel={rangeLabelOf(listFilter)}
          />
        ))}
      </div>
    ) : null}
  </div>
);

const ContentsData = (props: Omit<ContentsBodyProps, 'list'>) => {
  const { data: list } = useSuspenseQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/partners/links']({
      query: filterToLinkQuery(props.listFilter),
    }),
    select: contextOf,
  });

  return <ContentsBody list={list} {...props} />;
};

interface PartnersContentsScreenProps {
  isGuest: boolean;
}

export const PartnersContentsScreen = ({
  isGuest,
}: PartnersContentsScreenProps) => {
  const searchParams = useSearchParams();
  const [filter, setFilter] = useState(() =>
    filterOfSearchParams(Object.fromEntries(searchParams)),
  );
  const deferredFilter = useDeferredValue(filter);
  const { hasLinkYN, suspendYN } = usePartnersMember(isGuest);
  const hasLinks = !isGuest && hasLinkYN;
  const { data: firstDate = todayOf() } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/partners/links']({
      query: { to: todayOf() },
    }),
    select: (response) => firstDateOf(contextOf(response).contents),
    enabled: hasLinks,
  });
  const appliedFilter = resolvedFilterOf(filter, firstDate);
  const listFilter = resolvedFilterOf(deferredFilter, firstDate);
  const filterBar = (
    <ReportFilterBar
      key={`${appliedFilter.channel}~${appliedFilter.start}~${appliedFilter.end}~${appliedFilter.isAll}`}
      filter={appliedFilter}
      onApply={(next) => {
        setFilter(next);
        replaceFilterUrl(next);
      }}
      firstDate={firstDate}
    />
  );

  return (
    <PartnersShell
      mobileTitle="내 콘텐츠"
      mobileDescription="링크별 클릭·기부·보상을 확인해요."
    >
      <PartnersGate
        isGuest={isGuest}
        hasLinks={hasLinks}
        placeholder={
          <ContentsBody
            list={null}
            listFilter={appliedFilter}
            filterBar={filterBar}
          />
        }
      >
        <ErrorBoundary fallback={<SectionLoadError />}>
          <Suspense
            fallback={
              <ContentsBody
                list={null}
                listFilter={listFilter}
                filterBar={filterBar}
              />
            }
          >
            <ContentsData listFilter={listFilter} filterBar={filterBar} />
          </Suspense>
        </ErrorBoundary>
      </PartnersGate>
      <PartnersDialogs
        isGuest={isGuest}
        hasLinks={hasLinkYN}
        isSuspended={suspendYN}
      />
    </PartnersShell>
  );
};
